import moment, { type Moment } from "moment";
import { type ThunkDispatch } from "redux-thunk";
import { UPLOAD_ERROR_EVENT } from "#/_Bootstrap/GlobalConstants.ts";
import { store } from "#/_Bootstrap/RoutingAndStore.ts";
import { REQUEST_REGISTER } from "#/Actions/General/GeneralActionConstants.ts";
import { changeRegister, updateOutlet, updateRegister } from "#/Actions/General/LocationActions.ts";
import { setSaleKeys } from "#/Actions/SellScreen/SellScreenSaleKeysActions.ts";
import { updateOpenTime, updateSyncPage, updateSyncStatus } from "#/Actions/Synchronisation/SynchronisationActions.ts";
import { userLogout } from "#/Actions/Users/UserActions.ts";
import { fireEventType } from "#/Utilities/Generic/ActionsHelper.ts";
import { getLogLevel, LogLevel } from "#/Utilities/Platform/Logging.ts";
import {
    deleteOption,
    deleteTempOption,
    getOption,
    type Options,
    setOption,
    subscribeToOption,
} from "#/Utilities/Platform/Options/Options.ts";
import { offline, online } from "../../Actions/Information/InformationActions";
import PopupManager from "../../Common/Popups/PopupManager";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import { NAVIGATION_USER_UPDATE } from "../../Layout/Navigation/Navigation";
import FontAwesomeSolid from "../../Utilities/Platform/FontAwesome/FontAwesomeSolid";
import BaseAgent, { generateWorkerURL } from "../Bases/BaseAgent";
import { runUploadQueue } from "../Upload/UploadAgent";
import { UPLOAD_TYPES } from "../Upload/UploadEvents";
import { localTimeMismatchWarning } from "./LocalTimeMismatchWarning";
import {
    AGENT_EVENTS,
    FORCE_SYNC_FLAG,
    FORCE_SYNC_FLAG_KEY,
    INVOICE_NUMBER_STALE_SYNC_DAYS,
    INVOICE_NUMBER_SYNC_TIMEOUT_MS,
    LOCAL_INITIAL_DATA,
    type LocalInitialDataForPassing,
    POS_FORCE_SYNC,
    SYNC_EVENTS,
} from "./SyncEvents";

const SYNC_AGENT_POPUP_ID = "synchronisation-large";
const SYNC_FLAG_WARNING_ID = "synchronisation-flag-warning";

declare global {
    interface DocumentEventMap {
        synchronisationFinished: CustomEvent<{
            updated: {
                products: boolean;
                customers: boolean;
                promotions: boolean;
                priceLists: boolean;
            };
        }>;
    }
}

let syncFlagWarning = false;

export default class SyncAgent extends BaseAgent {
    protected register: null | string;
    protected outlet: null | string;
    protected globalMode: boolean;
    protected syncing: boolean;
    protected page: false | number;
    protected popup: boolean;
    protected needFullSync: boolean;

    /**
     * When the sync currently delivering data was requested (SYNC_START fires before its first request), so
     * a sync that was already running when an invoice number override happened can be told apart from one
     * that will have picked the override up
     */
    protected syncStartedAt = 0;

    /**
     * Resolves once a sync has actually applied `register_current_invoice_number` for the register this
     * agent is currently tracking - see waitForInvoiceNumberSync(). Recreated whenever the tracked register
     * changes, so each register "session" gets its own gate.
     */
    protected invoiceNumberSyncPromise: Promise<void>;
    protected resolveInvoiceNumberSync: () => void = () => {};

    constructor() {
        super(generateWorkerURL("syncworker"), "Shopfront Synchronisation");

        // Bind the listeners
        this.handleMessage = this.handleMessage.bind(this);
        this.handleStoreChange = this.handleStoreChange.bind(this);
        this.handleForceSync = this.handleForceSync.bind(this);

        // Store the current register and outlet
        this.register = null;
        this.outlet = null;
        this.globalMode = false;

        // Store the status of the synchronisation and the page
        this.syncing = false;
        this.page = false;
        this.popup = false;
        this.needFullSync = false;

        this.invoiceNumberSyncPromise = new Promise(res => this.resolveInvoiceNumberSync = res);

        // Start the worker
        this.startWorker();

        // Subscribe to register changes
        store.subscribe(this.handleStoreChange);

        // Subscribe to option changes
        this.handleInstanceChange(
            "_instance_id",
            subscribeToOption("_instance_id", this.handleInstanceChange),
            ""
        );

        // Listen for force sync
        if(typeof document !== "undefined") {
            document.addEventListener(POS_FORCE_SYNC, this.handleForceSync);
        }
    }

    protected handleForceSync(e: Partial<CustomEvent>): void {
        void this.performSync(e.detail.full);
    }

    protected handleRequestLocationMessage(): void {
        this.syncing = false;

        const register = getOption("register");
        const outlet = getOption("outlet");
        const global = getOption("global_mode");

        if(register) {
            this.sendMessage(AGENT_EVENTS.REGISTER, register);
        } else if(outlet) {
            this.sendMessage(AGENT_EVENTS.OUTLET, outlet);
        } else if(global) {
            this.globalMode = true;
            this.sendMessage(AGENT_EVENTS.GLOBAL_MODE, true);
        } else {
            store.dispatch(fireEventType(REQUEST_REGISTER));
        }
    }

    /**
     * Whether a routine sync's `register_current_invoice_number` should be ignored.
     *
     * The server only reports the highest number it has been told about, which lags behind whichever device
     * is currently selling, so a routine sync can only move the counter forwards - taking a lower one hands
     * out numbers that are already in use. Moving it backwards is only legitimate as a deliberate override,
     * which arrives separately (see handleOverrideInvoiceNumberRequest).
     */
    protected async shouldIgnoreSyncedInvoiceNumber(incoming: unknown): Promise<boolean> {
        const stored = localStorage.getItem("register_current_invoice_number");

        if(stored === null) {
            return false;
        }

        const overriddenAt = getOption("register_current_invoice_number_overridden_at");

        // A sync already in flight when the override happened is reporting the number it replaced. A page
        // that connected mid-sync never saw its SYNC_START, so it can't make that call and shouldn't try
        if(overriddenAt !== null && this.syncStartedAt !== 0 && this.syncStartedAt <= overriddenAt) {
            return true;
        }

        if(await this.hasOutdatedInvoiceNumber()) {
            return false;
        }

        const current = Number(stored);
        const next = Number(incoming);

        if(Number.isNaN(current) || Number.isNaN(next)) {
            return false;
        }

        return next <= current;
    }

    /**
     * Whether any sales are still sitting in the local upload queue, waiting to be sent to the server
     */
    protected async hasPendingSaleUploads(): Promise<boolean> {
        const source = await determineDataSource();
        const uploads = await source.uploads.all();

        return uploads.some(upload => upload.type === UPLOAD_TYPES.SALE);
    }

    /**
     * Whether this device has been away long enough that an override can no longer reach it as a queue
     * event, leaving its cached counter the less reliable of the two.
     *
     * Sales still waiting to upload keep the local counter authoritative however long it's been, since the
     * server can't have seen those numbers yet.
     */
    protected async hasOutdatedInvoiceNumber(): Promise<boolean> {
        const lastSync = getOption("lastsync");

        if(!lastSync) {
            return false;
        }

        const syncedAt = moment(lastSync);
        const staleFrom = moment().subtract(INVOICE_NUMBER_STALE_SYNC_DAYS, "days");

        if(!syncedAt.isValid() || syncedAt.isAfter(staleFrom)) {
            return false;
        }

        return !await this.hasPendingSaleUploads();
    }

    /**
     * Applies the settings a sync has reported to local storage
     */
    protected async handleUpdateLocalStorageRequest(data: any): Promise<void> {
        const newStorage = data;
        let sawInvoiceNumber = false;

        for(const i in newStorage) {
            if(!Object.prototype.hasOwnProperty.call(newStorage, i)) continue;

            if(i === "cost_calculation_method") {
                const old = getOption(i);

                if(old !== newStorage[i] && (old === "last" || newStorage[i] === "last")) {
                    // Need to perform a full sync
                    this.needFullSync = true;
                }
            } else if(i === "outlet_use_global_cost") {
                const old = getOption(i);

                if(old !== newStorage[i]) {
                    // Need to perform a full sync
                    this.needFullSync = true;
                }
            } else if(i === "register_current_invoice_number") {
                sawInvoiceNumber = true;

                if(await this.shouldIgnoreSyncedInvoiceNumber(newStorage[i])) {
                    continue;
                }
            } else if(this.shouldUpdateSaleKeys(i, newStorage)) {
                store.dispatch(setSaleKeys(newStorage[i]));
            }

            setOption(i as keyof Options, newStorage[i]);
        }

        // Applied or skipped, this session now has an answer for anything awaiting the invoice number
        if(sawInvoiceNumber) {
            this.clearAcknowledgedInvoiceNumberOverride();
            this.resolveInvoiceNumberSync();
        }
    }

    /**
     * Clears what this device had counted for a taking, since a synced change to its amount makes both the
     * counted total and the calculator's denomination breakdown wrong. They're keyed by the payment method
     * the taking belongs to
     */
    protected handleTakingsChangeRequest(taking: string): void {
        deleteOption(`close_register_${taking}`);
        deleteTempOption(taking);
    }

    /**
     * Forgets an override once a sync that started after it has reported back, since that sync's number
     * already accounts for it. Without this the marker would sit there for the life of the register and
     * keep tripping the check above
     */
    protected clearAcknowledgedInvoiceNumberOverride(): void {
        const overriddenAt = getOption("register_current_invoice_number_overridden_at");

        if(overriddenAt !== null && this.syncStartedAt > overriddenAt) {
            deleteOption("register_current_invoice_number_overridden_at");
        }
    }

    /**
     * Applies a deliberate invoice number override made on any device on this register. Unlike the number a
     * routine sync reports, this is allowed to move the counter backwards
     */
    protected handleOverrideInvoiceNumberRequest(invoiceNumber: number): void {
        console.log(
            "Overriding invoice number from",
            getOption("register_current_invoice_number"),
            "to",
            invoiceNumber
        );

        setOption("register_current_invoice_number", invoiceNumber);
        setOption("register_current_invoice_number_overridden_at", Date.now());

        this.resolveInvoiceNumberSync();
    }

    protected handleOpenCloseTimeRequest(data: {
        openTime: string;
        closeTime: string;
    }): void {
        const openTime = data.openTime;
        let closeTime: string | Moment = data.closeTime;

        let localOpenTime: string | Moment = getOption("register_open_time");

        if(localOpenTime === "" || openTime !== "") {
            store.dispatch(updateOpenTime(data.openTime));
        } else {
            localOpenTime = moment(localOpenTime);
            closeTime = moment(closeTime);

            if(localOpenTime.isBefore(closeTime)) {
                store.dispatch(updateOpenTime(""));
            }
        }
    }

    protected handleRequestInitialDataRequest(): void {
        const initialData = {} as LocalInitialDataForPassing;

        for(const key of LOCAL_INITIAL_DATA) {
            (initialData[key] as Options[typeof key]) = getOption(key);
        }

        this.sendMessage(AGENT_EVENTS.INITIAL_DATA, JSON.stringify(initialData));
    }

    protected handleMessage(data: {
        type: SYNC_EVENTS;
        message: any;
    }): void {
        if(!data.type) {
            console.error("Unknown data passed to SyncAgent", data);

            return;
        }

        switch(data.type) {
            case SYNC_EVENTS.SYNC_START:
                store.dispatch(updateSyncStatus("running"));
                this.syncing = true;
                this.syncStartedAt = Date.now();
                this.needFullSync = false;
                break;
            case SYNC_EVENTS.SYNC_END:
                if(data.message) {
                    syncFlagWarning = false;

                    PopupManager.remove(FORCE_SYNC_FLAG_KEY);

                    setOption("lastsync", data.message.lastSync);

                    if(typeof data.message.flag !== "undefined") {
                        setOption(FORCE_SYNC_FLAG_KEY, data.message.flag);
                    }
                }

                store.dispatch(updateSyncStatus("stopped"));
                this.syncing = false;

                // Broadcast the end of the sync
                if(typeof document !== "undefined" && this.page !== false) {
                    document.dispatchEvent(new CustomEvent("synchronisationFinished", {
                        detail: {
                            updated: data.message.updated,
                        },
                    }));

                    document.dispatchEvent(new Event(NAVIGATION_USER_UPDATE));
                }

                this.page = false;

                if(this.needFullSync) {
                    this.handleForceSync({
                        detail: {
                            full: true,
                        },
                    });
                }

                break;
            case SYNC_EVENTS.SYNC_PAGE:
                if(data.message < 2) { // Update on the first and second page
                    document.dispatchEvent(new Event(NAVIGATION_USER_UPDATE));
                }

                store.dispatch(updateSyncPage(data.message));
                this.page = data.message;
                break;
            case SYNC_EVENTS.SYNC_ERROR:
                store.dispatch(updateSyncStatus("error"));
                this.syncing = false;
                break;
            case SYNC_EVENTS.REQUEST_LOCATION:
                this.handleRequestLocationMessage();

                break;
            case SYNC_EVENTS.INVALIDATE_REGISTER:
                this.syncing = false;
                store.dispatch(fireEventType(REQUEST_REGISTER));
                break;
            case SYNC_EVENTS.UPDATE_LOCALSTORAGE:
                void this.handleUpdateLocalStorageRequest(data.message);
                break;
            case SYNC_EVENTS.OVERRIDE_INVOICE_NUMBER:
                this.handleOverrideInvoiceNumberRequest(data.message);
                break;
            case SYNC_EVENTS.REQUEST_LAST_SYNC:
                this.sendMessage(AGENT_EVENTS.LAST_SYNC, getOption("lastsync"));
                break;
            case SYNC_EVENTS.UPDATE_UTC_OFFSET:
                setOption("utc_offset", data.message.offset);
                localTimeMismatchWarning(data.message.offset, data.message.localTime, data.message.serverTime);
                break;
            case SYNC_EVENTS.OPEN_CLOSE_TIME:
                this.handleOpenCloseTimeRequest(data.message);
                break;
            case SYNC_EVENTS.REQUEST_INITIAL_DATA:
                this.handleRequestInitialDataRequest();
                break;
            case SYNC_EVENTS.TAKINGS_CHANGE:
                this.handleTakingsChangeRequest(data.message);
                break;
            case SYNC_EVENTS.UPDATE_REGISTER:
                (store.dispatch as ThunkDispatch<any, any, any>)(updateRegister(data.message));
                break;
            case SYNC_EVENTS.UPDATE_OUTLET:
                (store.dispatch as ThunkDispatch<any, any, any>)(updateOutlet(data.message));
                break;
            case SYNC_EVENTS.UPDATE_GLOBAL_MODE:
                (store.dispatch as ThunkDispatch<any, any, any>)(changeRegister("global", undefined, false));
                break;
            case SYNC_EVENTS.SYNC_OFFLINE:
                (store.dispatch as ThunkDispatch<any, any, any>)(offline());
                break;
            case SYNC_EVENTS.SYNC_ONLINE:
                (store.dispatch as ThunkDispatch<any, any, any>)(online());
                break;
            case SYNC_EVENTS.SYNC_UNAUTHENTICATED:
                if(getLogLevel() === LogLevel.VERBOSE) {
                    console.log("Logging out because of synchronisation");
                }

                (store.dispatch as ThunkDispatch<any, any, any>)(userLogout());
                break;
            case SYNC_EVENTS.FORCE_UPLOAD:
                console.log("Forcing an upload due to sync queue");
                runUploadQueue(true, data.message.reset, true)
                    .then(() => {
                        this.sendMessage(AGENT_EVENTS.DATA_RESPONSE, data.message.returnId);
                    });
                break;
            case SYNC_EVENTS.OUTSTANDING_UPLOADS:
                if(typeof document !== "undefined") {
                    document.dispatchEvent(new CustomEvent(UPLOAD_ERROR_EVENT, {
                        detail: {
                            outstanding: true,
                        },
                    }));
                }

                if(getOption(FORCE_SYNC_FLAG_KEY) !== FORCE_SYNC_FLAG && syncFlagWarning) {
                    syncFlagWarning = true;

                    PopupManager.create({
                        id     : SYNC_FLAG_WARNING_ID,
                        message: "Outstanding uploads are preventing Shopfront from performing a full sync. " +
                            "Your current data is out of date and could cause unexpected errors. Please " +
                            "resolve the outstanding uploads and perform a full synchronisation.",
                        icon   : "exclamation",
                        classes: [ "popup-error" ],
                        timeout: 0,
                    });
                } else {
                    PopupManager.warning(
                        "Unable to perform a full synchronisation as there are outstanding uploads in the queue"
                    );
                }

                break;
            default:
                console.error("Unknown data passed to SyncAgent");
        }

        this.checkSyncStatus();
    }

    protected handleStoreChange(): void {
        const state = store.getState();

        if(state.location.isGlobal && !this.globalMode) {
            this.sendMessage(AGENT_EVENTS.GLOBAL_MODE, true);
            this.globalMode = true;

            return;
        }

        if(state.location.outlet === null) {
            this.outlet = null;
            this.register = null;

            return;
        }

        if(this.outlet !== state.location.outlet.id) {
            this.outlet = state.location.outlet.id;

            if(state.location.register && this.register !== state.location.register.id) {
                this.register = state.location.register.id;
                this.resetInvoiceNumberSync();
                this.sendMessage(AGENT_EVENTS.REGISTER, this.register);
            } else {
                this.sendMessage(AGENT_EVENTS.OUTLET, this.outlet);
            }
        } else if(state.location.register && this.register !== state.location.register.id) {
            this.register = state.location.register.id;
            this.resetInvoiceNumberSync();
            this.sendMessage(AGENT_EVENTS.REGISTER, this.register);
        }
    }

    /**
     * Starts a fresh "invoice number synced" gate for the newly-active register, so switching registers
     * within the same tab (e.g. taking over/taking control) also waits for its own authoritative sync
     * instead of reusing whatever the previous register's session already resolved.
     */
    protected resetInvoiceNumberSync(): void {
        this.invoiceNumberSyncPromise = new Promise(res => this.resolveInvoiceNumberSync = res);
    }

    protected handleInstanceChange = (_: "_instance_id", newValue: string | false, oldValue: string | false): void => {
        if(newValue !== oldValue) {
            this.sendMessage(AGENT_EVENTS.INSTANCE_ID, newValue);
        }
    };

    protected checkSyncStatus(): void {
        if(this.syncing && this.page as number > 1) {
            this.showPopup();
        } else {
            this.hidePopup();
        }
    }

    protected showPopup(): void {
        if(this.popup) {
            return;
        }

        PopupManager.create({
            id     : SYNC_AGENT_POPUP_ID,
            message: "Performing a large synchronisation...",
            icon   : <FontAwesomeSolid icon="spinner-third" spin />,
            classes: [ "popup-info" ],
            timeout: 0,
        });

        this.popup = true;
    }

    protected hidePopup(): void {
        if(!this.popup) {
            return;
        }

        PopupManager.remove(SYNC_AGENT_POPUP_ID);

        this.popup = false;
    }

    protected shouldUpdateSaleKeys(key: string, newOptions: Record<string, any>): boolean {
        if(key !== "register_default_sale_keys" && key !== "outlet_default_sale_keys" && key !== "default_sale_keys") {
            return false;
        }

        if(key === "register_default_sale_keys") {
            return newOptions["register_default_sale_keys"] !== getOption("register_default_sale_keys") &&
                newOptions["register_default_sale_keys"] !== "default";
        }

        const register = newOptions["register_default_sale_keys"] || getOption("register_default_sale_keys");

        if(key === "outlet_default_sale_keys") {
            if(register === "default") {
                return newOptions["outlet_default_sale_keys"] !== getOption("outlet_default_sale_keys") &&
                    newOptions["outlet_default_sale_keys"] !== "default";
            } else {
                return false;
            }
        }

        const outlet = newOptions["outlet_default_sale_keys"] || getOption("outlet_default_sale_keys");

        if(key === "default_sale_keys") {
            if(register === "default" && outlet === "default") {
                return newOptions["default_sale_keys"] !== getOption("default_sale_keys") &&
                    newOptions["default_sale_keys"] !== "default";
            } else {
                return false;
            }
        }

        return false;
    }

    public isSyncing(): boolean {
        return this.syncing;
    }

    /**
     * Resolves once this session has an authoritative answer for `register_current_invoice_number` - either
     * because a sync has actually applied one (see handleUpdateLocalStorageRequest), the device is offline
     * (nothing to wait for), or a bounded timeout has elapsed as a safety net so a stalled/slow sync can
     * never block a sale indefinitely.
     *
     * Intended to be awaited once per register session, right before generating a new invoice number
     * (generateInvoiceId) - subsequent calls resolve immediately once the first sync has landed.
     */
    public waitForInvoiceNumberSync(): Promise<void> {
        if(!store.getState().information.online) {
            return Promise.resolve();
        }

        return Promise.race([
            this.invoiceNumberSyncPromise,
            new Promise<void>(res => setTimeout(res, INVOICE_NUMBER_SYNC_TIMEOUT_MS)),
        ]);
    }

    public performSync(fullSync: boolean): Promise<void> {
        if(fullSync) {
            setOption("lastsync", "");
        }

        return new Promise((res) => {
            this.sendMessage(AGENT_EVENTS.FORCE_SYNC, {
                shouldClearTakings: fullSync,
            }, res);
        });
    }
}
